import { JobCountResponse } from '@doda/api-client/aspida/@types';
import { appQueryParamsSelector } from '@doda/common/src/redux/features/queryParams';
import {
  JobCountQuery,
  getJobCount,
} from '@doda/common/src/services/getJobCountAPI';
import { removeUndefinedFromObject } from '@doda/common/src/utils/objectUtil';
import { Omit } from '@reduxjs/toolkit/dist/tsHelpers';
import { useRouteStation } from 'components/SearchSideBar/BasicInfo/LocationArea/RouteStation/hooks/useRouteStation';
import { useLocation } from 'components/SearchSideBar/BasicInfo/LocationArea/hooks/useLocation';
import { useOccupation } from 'components/SearchSideBar/BasicInfo/OccupationArea/hooks/useOccupation';
import { InitialSideBarMasterDataType } from 'components/SearchSideBar/BasicInfo/types';
import { useJobOfferKind } from 'components/SearchSideBar/SearchDetailCondition/hooks/useJobOfferKind';
import { FeaturedSearchConditionQuery } from 'components/SearchSideBar/types';
import { SAVED_CONDITION_LIMIT } from 'constants/searchCondition';
import { selectedCurrentConditionCount } from 'logics/selectedCurrentConditionCount';
import { useState } from 'react';
import { useSelector } from 'react-redux';

/**
 * useSearchConditionに渡される引数を定義
 */
interface ArgumentType {
  /** 初期サイドバーマスターデータ（業種を除く） */
  initialSideBarMasterData: Omit<InitialSideBarMasterDataType, 'industry'>;
  /** ジョブカウントのレスポンスデータ */
  jobCountData: JobCountResponse;
  /** 特集検索条件 */
  featuredSearchCondition: FeaturedSearchConditionQuery;
}

/**
 * 特集枠案件一覧画面サイドバー用検索条件カスタムフック
 *
 * @param {ArgumentType} root0 - カスタムフックに渡される引数
 * @returns {Object} - サイドバーとモーダルの検索条件と操作
 */
export const useSearchCondition = ({
  initialSideBarMasterData,
  jobCountData,
  featuredSearchCondition,
}: ArgumentType) => {
  const { tp } = useSelector(appQueryParamsSelector);

  const occupation = useOccupation({
    initialOccupationData: initialSideBarMasterData.occupation,
  });

  const location = useLocation({
    initialLocationData: initialSideBarMasterData.location,
  });

  const routeStation = useRouteStation({
    initialRouteStationData: initialSideBarMasterData.routeStation,
  });

  const {
    jobOfferKindList,
    selectedJobOfferKindList,
    updateIsCheckedJobOfferKindList,
    updateJobOfferKindListJobCount,
  } = useJobOfferKind({
    featuredSearchCondition,
    query: tp,
    jobCountData,
  });

  const [totalCount, setTotalCount] = useState(jobCountData.totalCount ?? 0);
  const [modalTotalCount, setModalTotalCount] = useState(
    jobCountData.totalCount ?? 0
  );

  const [isConditionUpperLimitError, setIsConditionUpperLimitError] =
    useState<boolean>(true);

  const selectedCurrentCondition = {
    jid: featuredSearchCondition.jid,
    searchJobTypeLId: occupation.selectedOccupationLIdList,
    searchJobTypeMId: occupation.selectedOccupationMIdList,
    searchJobTypeSId: occupation.selectedOccupationSIdList,
    areaId: location.selectedAreaIdList,
    prefectureId: location.selectedPrefectureIdList,
    cityId: location.selectedCityIdList,
    wardId: location.selectedWardIdList,
    routeId: routeStation.selectedRouteIdList,
    stationId: routeStation.selectedStationIdList,
    industryLId: featuredSearchCondition.industryLId ?? [],
    industrySId: featuredSearchCondition.industrySId ?? [],
    employmentStatus: featuredSearchCondition.employmentStatus ?? [],
    lowerLimitIncome: featuredSearchCondition.lowerLimitIncome,
    upperLimitIncome: featuredSearchCondition.upperLimitIncome,
    isNewArrival: featuredSearchCondition.isNewArrival ?? false,
    isNearDeadline: featuredSearchCondition.isNearDeadline ?? false,
    includedKeywordCategory: featuredSearchCondition.includedKeywordCategory,
    excludedKeywordCategory: featuredSearchCondition.excludedKeywordCategory,
    includedKeyword: featuredSearchCondition.includedKeyword ?? [],
    excludedKeyword: featuredSearchCondition.excludedKeyword ?? [],
    holidayAndWayOfWorking:
      featuredSearchCondition.holidayAndWayOfWorking ?? [],
    requirements: featuredSearchCondition.requirements ?? [],
    workplaceEnvironment: featuredSearchCondition.workplaceEnvironment ?? [],
    welfareBenefits: featuredSearchCondition.welfareBenefits ?? [],
    languageSkills: featuredSearchCondition.languageSkills ?? [],
    positions: featuredSearchCondition.positions ?? [],
    employeeAverageAge: featuredSearchCondition.employeeAverageAge ?? [],
    employNum: featuredSearchCondition.employNum ?? [],
    establish: featuredSearchCondition.establish ?? [],
    jobOfferKind: selectedJobOfferKindList,
  };

  /**
   * ジョブカウントを更新する関数
   *
   * @param {JobCountQuery} [currentCondition] - 現在の条件
   * @param {boolean} [isToggleOccupation] - 職種のトグル設定
   */
  const updateJobCount = async (
    currentCondition?: JobCountQuery,
    isToggleOccupation?: boolean
  ) => {
    const conditions = {
      ...selectedCurrentCondition,
      ...currentCondition,
    };

    setIsConditionUpperLimitError(
      selectedCurrentConditionCount(conditions) <= SAVED_CONDITION_LIMIT
    );

    const res = await getJobCount(removeUndefinedFromObject(conditions));

    setTotalCount(res.totalCount ?? 0);

    occupation.updateOccupationJobCount(
      res.searchJobTypeLList,
      res.searchJobTypeMList,
      res.searchJobTypeSList,
      isToggleOccupation ?? false
    );
    location.updateLocationJobCount(
      res.areaList,
      res.prefectureList,
      res.cityList,
      res.wardList
    );
    routeStation.updateRouteStationJobCount(res.routeList, res.stationList);
    updateJobOfferKindListJobCount(res.jobOfferKindList);
  };

  /**
   * モーダルに渡す件数更新処理
   *
   * @param {JobCountQuery} [currentCondition] - 現在の条件
   */
  const updateModalJobCount = async (currentCondition?: JobCountQuery) => {
    const conditions = {
      ...selectedCurrentCondition,
      ...currentCondition,
    };

    setIsConditionUpperLimitError(
      selectedCurrentConditionCount(conditions) <= SAVED_CONDITION_LIMIT
    );

    const res = await getJobCount(removeUndefinedFromObject(conditions));

    setModalTotalCount(res.totalCount ?? 0);
  };

  return {
    occupation: {
      occupationDisplayList: occupation.occupationDisplayList,
      updateIsCheckedOccupationLList: occupation.updateIsCheckedOccupationLList,
      updateIsCheckedOccupationMList: occupation.updateIsCheckedOccupationMList,
      updateIsCheckedOccupationSList: occupation.updateIsCheckedOccupationSList,
    },
    location: {
      locationDisplayList: location.locationDisplayList,
      updateIsCheckedAreaList: location.updateIsCheckedAreaList,
      updateIsCheckedPrefectureList: location.updateIsCheckedPrefectureList,
      updateIsCheckedCityList: location.updateIsCheckedCityList,
      updateIsCheckedWardList: location.updateIsCheckedWardList,
    },
    routeStation: {
      routeStationDisplayList: routeStation.routeStationDisplayList,
      updateIsCheckedRouteList: routeStation.updateIsCheckedRouteList,
      updateIsCheckedStationList: routeStation.updateIsCheckedStationList,
    },
    jobOfferKind: {
      jobOfferKindList,
      updateIsCheckedJobOfferKindList,
    },
    selectedCurrentCondition,
    updateJobCount,
    updateModalJobCount,
    totalCount,
    modalTotalCount,
    isConditionUpperLimitError,
  };
};
